/**
 * SMSWiser paths, landers, offers, sources stats queries
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQuery } from '@tanstack/react-query';

import type {
  PathsWithStatsResponse,
  LandersWithStatsResponse,
  OffersWithStatsResponse,
  PhonesWithStatsResponse,
  SourcesWithStatsResponse,
} from '@/features/smswiser-campaigns/types/workspace.types';
import api from '@/services/api';

import { keys } from '../keys';
import { useWithStatsTracker } from '../stats-tracker';
import { unwrapApiData } from '../utils';

export interface PathsWithStatsInput {
  countryKey: string;
  trafficCampaignId?: string;
  wiserIds: number[];
  from?: string;
  to?: string;
  tz?: string;
  currency?: string;
  limit?: number;
  enabled?: boolean;
}

export type LandersWithStatsInput = PathsWithStatsInput;
export type OffersWithStatsInput = PathsWithStatsInput;

export interface SourcesWithStatsInput {
  countryKey: string;
  wiserIds: number[];
  from?: string;
  to?: string;
  tz?: string;
  currency?: string;
  enabled?: boolean;
}

function buildTrafficStatsParams(
  input: { trafficCampaignId?: string; wiserIds: number[]; from?: string; to?: string; tz?: string; currency?: string; limit?: number },
): Record<string, string> {
  const wiserIds = input.wiserIds.length > 0 ? input.wiserIds : [0];
  const params: Record<string, string> = {};
  if (input.trafficCampaignId) {
    params.trafficCampaignId = input.trafficCampaignId;
    params.voluumCampaignId = input.trafficCampaignId;
  }
  if (wiserIds[0] > 0) params.wiserIds = wiserIds.join(',');
  if (input.from) params.from = input.from;
  if (input.to) params.to = input.to;
  if (input.tz) params.tz = input.tz;
  if (input.currency) params.currency = input.currency;
  if (input.limit != null && input.limit > 0) params.limit = String(input.limit);
  return params;
}

export function usePathsWithStats(input: PathsWithStatsInput) {
  const wiserIds = input.wiserIds.length > 0 ? input.wiserIds : [0];
  const params = useWithStatsTracker(buildTrafficStatsParams(input));

  return useQuery({
    queryKey: keys.pathsWithStats(input.countryKey, input.trafficCampaignId, wiserIds, params),
    queryFn: async () => {
      const res = await api.get(
        `/smswiser/campaigns/${input.countryKey}/paths-with-stats`,
        { params },
      );
      return unwrapApiData<PathsWithStatsResponse>(res.data);
    },
    enabled:
      (input.enabled ?? true) &&
      (Boolean(input.trafficCampaignId) || (wiserIds.length > 0 && wiserIds[0] > 0)) &&
      Boolean(input.from) &&
      Boolean(input.to),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnMount: 'always',
  });
}

export function useLandersWithStats(input: LandersWithStatsInput) {
  const wiserIds = input.wiserIds.length > 0 ? input.wiserIds : [0];
  const params = useWithStatsTracker(buildTrafficStatsParams(input));

  return useQuery({
    queryKey: keys.landersWithStats(input.countryKey, input.trafficCampaignId, wiserIds, params),
    queryFn: async () => {
      const res = await api.get(
        `/smswiser/campaigns/${input.countryKey}/landers-with-stats`,
        { params },
      );
      return unwrapApiData<LandersWithStatsResponse>(res.data);
    },
    enabled:
      (input.enabled ?? true) &&
      (Boolean(input.trafficCampaignId) || (wiserIds.length > 0 && wiserIds[0] > 0)) &&
      Boolean(input.from) &&
      Boolean(input.to),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnMount: 'always',
  });
}

export function useOffersWithStats(input: OffersWithStatsInput) {
  const wiserIds = input.wiserIds.length > 0 ? input.wiserIds : [0];
  const params = useWithStatsTracker(buildTrafficStatsParams(input));

  return useQuery({
    queryKey: keys.offersWithStats(input.countryKey, input.trafficCampaignId, wiserIds, params),
    queryFn: async () => {
      const res = await api.get(
        `/smswiser/campaigns/${input.countryKey}/offers-with-stats`,
        { params },
      );
      return unwrapApiData<OffersWithStatsResponse>(res.data);
    },
    enabled:
      (input.enabled ?? true) &&
      (Boolean(input.trafficCampaignId) || (wiserIds.length > 0 && wiserIds[0] > 0)) &&
      Boolean(input.from) &&
      Boolean(input.to),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnMount: 'always',
  });
}

export interface PhonesWithStatsInput {
  countryKey: string;
  wiserIds: number[];
  from?: string;
  to?: string;
  tz?: string;
  currency?: string;
  limit?: number;
  enabled?: boolean;
}

export function usePhonesWithStats(input: PhonesWithStatsInput) {
  const wiserIds = input.wiserIds.length > 0 ? input.wiserIds : [0];
  const params: Record<string, string> = useWithStatsTracker({ wiserIds: wiserIds.join(',') });
  if (input.from) params.from = input.from;
  if (input.to) params.to = input.to;
  if (input.tz) params.tz = input.tz;
  if (input.currency) params.currency = input.currency;
  if (input.limit != null) params.limit = input.limit === 0 ? 'all' : String(input.limit);

  return useQuery({
    queryKey: keys.phonesWithStats(input.countryKey, wiserIds, params),
    queryFn: async () => {
      const res = await api.get(
        `/smswiser/campaigns/${input.countryKey}/phones-with-stats`,
        { params },
      );
      return unwrapApiData<PhonesWithStatsResponse>(res.data);
    },
    enabled:
      (input.enabled ?? true) &&
      wiserIds.length > 0 &&
      wiserIds[0] > 0 &&
      Boolean(input.from) &&
      Boolean(input.to),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}

export function useSourcesWithStats(input: SourcesWithStatsInput) {
  const wiserIds = input.wiserIds.length > 0 ? input.wiserIds : [0];
  const params: Record<string, string> = useWithStatsTracker({ wiserIds: wiserIds.join(',') });
  if (input.from) params.from = input.from;
  if (input.to) params.to = input.to;
  if (input.tz) params.tz = input.tz;
  if (input.currency) params.currency = input.currency;

  return useQuery({
    queryKey: keys.sourcesWithStats(input.countryKey, wiserIds, params),
    queryFn: async () => {
      const res = await api.get(
        `/smswiser/campaigns/${input.countryKey}/sources-with-stats`,
        { params },
      );
      return unwrapApiData<SourcesWithStatsResponse>(res.data);
    },
    enabled:
      (input.enabled ?? true) &&
      wiserIds.length > 0 &&
      wiserIds[0] > 0 &&
      Boolean(input.from) &&
      Boolean(input.to),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}
